import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import CsvHandler from './CsvHandler';
import { UploadCloud } from 'Utils/SvgIcons';
import service from 'Api/service';
import { saveError } from 'Api';
import { history } from 'Components/utils/router/history';
import ImportWizardHeader from './ImportWizardHeader';
import * as actions from 'Actions';
import {
  PRETTY_NAMES,
  downloadExampleCsv,
  followImportProgress,
} from 'HelperFunctions/import_wizard';

const INVENTORY_TYPES = ['item', 'accessory', 'purchase', 'bundle'];

// Release 38 review: prices, quantities and images are protected (ticked) by default and only for rows
// that update an existing record; new records always take the file's values.
const PROTECTED = [
  { key: 'excludePricing', param: 'exclude_pricing', label: 'Prices' },
  { key: 'excludeQuantity', param: 'exclude_quantity', label: 'Quantities' },
  { key: 'excludeImages', param: 'exclude_images', label: 'Images' },
];

const OPTIONS = [
  {
    key: 'noCreate',
    param: 'no_create',
    label: (noun) =>
      `Do not create new ${noun} (rows that match nothing are skipped and listed)`,
  },
  {
    key: 'allowNoPrice',
    param: 'allow_no_price',
    label: (noun) => `Allow new ${noun} without a price`,
    inventoryOnly: true,
  },
];

const isInventory = (dataType) => INVENTORY_TYPES.includes(dataType);

// Upload step: the file goes to the API as multipart (csv, xlsx or ods) and is staged in the
// background; the progress bar follows the ImportProgress on the ProgressChannel.
class ImportWizardUpload extends Component {
  state = {
    selectedFile: null,
    dataType: '',
    uploading: false,
    excludePricing: true,
    excludeQuantity: true,
    excludeImages: true,
    noCreate: false,
    allowNoPrice: false,
    message: '',
  };

  componentDidMount() {
    const { dataType } = this.props.match.params;
    this.setState({ dataType });
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.business &&
      !prevProps.business.permissions.importWizardEnabled
    ) {
      history.push('/unauthorized');
    }
  }

  fileChangeHandler = (event) => {
    if (!event.target.files?.length) return;
    this.setState({ selectedFile: event.target.files[0], message: '' });
  };

  handleAcceptFile = () => {
    const { selectedFile, dataType } = this.state;
    const { setErrors, startProgressBar, updateProgressBar, closeProgressBar } =
      this.props;
    if (!selectedFile) {
      this.setState({ message: 'Choose a .csv, .xlsx or .ods file first.' });
      return;
    }
    const data = new FormData();
    data.append('file', selectedFile);
    data.append('import_type', dataType);
    [...PROTECTED, ...OPTIONS].forEach((option) =>
      data.append(option.param, this.state[option.key] ? 'true' : 'false')
    );

    this.setState({ uploading: true, message: '' });
    service
      .post(`${import.meta.env.VITE_API_DOMAIN}/api/import_data`, data)
      .then((response) => {
        startProgressBar('import');
        return followImportProgress(response.data.import, (percent) =>
          updateProgressBar(percent)
        );
      })
      .then(() => {
        closeProgressBar();
        history.push(`/importWizard/adjust/${dataType}`);
      })
      .catch((error) => {
        closeProgressBar();
        const message =
          error?.response?.data ||
          error?.response?.message ||
          error?.response?.statusText ||
          'The file could not be read.';
        setErrors(
          typeof message === 'string' ? message : JSON.stringify(message)
        );
        saveError(error);
        this.setState({ uploading: false });
      });
  };

  checkbox = (option, label = option.label) => (
    <label key={option.key} className='importWizardToggle'>
      <input
        type='checkbox'
        id={`import_wizard_${option.param}`}
        checked={this.state[option.key]}
        onChange={(event) =>
          this.setState({ [option.key]: event.target.checked })
        }
      />
      {label}
    </label>
  );

  handleExampleDownload = (event) => {
    event.preventDefault();
    downloadExampleCsv(this.state.dataType).catch(() =>
      this.props.setErrors(
        'Unable to download the example CSV. Please try again.'
      )
    );
  };

  render() {
    const { uploading, selectedFile, dataType, message } = this.state;
    const noun = (PRETTY_NAMES[dataType] || 'records').toLowerCase();
    return (
      <div className='contentWrapper'>
        <ImportWizardHeader
          importStatus='upload'
          uploading={uploading}
          displayButtons={!uploading}
          onNextStep={this.handleAcceptFile}
          onCancel={() => history.push('/importWizard')}
          headerTitle={`Upload Your ${PRETTY_NAMES[dataType] || ''} File`}
        />
        <div className='ImportWizardUploadContainer'>
          <div className='importWizardUpload'>
            {!uploading ? (
              <CsvHandler
                fileHandler={this.fileChangeHandler}
                selectedFile={selectedFile}
              />
            ) : (
              <span className='importWizardUploading'>
                <UploadCloud />
                <p>Uploading and checking your rows...</p>
              </span>
            )}
            {message && <p className='importWizardMessage warn'>{message}</p>}
            <a
              className='example'
              href='#'
              onClick={this.handleExampleDownload}
            >
              See Example {PRETTY_NAMES[dataType] || ''} CSV
            </a>
          </div>
          {!uploading && (
            <div className='importWizardOptions'>
              <p className='importWizardMatchRule'>
                {dataType === 'customer' ? (
                  <>
                    Each row updates the customer with the same email (or first
                    and last name when there is no email).
                  </>
                ) : (
                  <>
                    Each row updates the record with the same name, or with the
                    same Token ID when the file has that column (e.g.{' '}
                    <code>#abc123</code>).
                  </>
                )}{' '}
                Rows that match nothing are created.
              </p>
              {isInventory(dataType) && (
                <fieldset className='importWizardProtected'>
                  <legend>Keep current values on existing {noun}</legend>
                  {PROTECTED.map((option) => this.checkbox(option))}
                  <small>
                    Untick a box to update it from the file. New {noun} always
                    use the file&apos;s values.
                  </small>
                </fieldset>
              )}
              {OPTIONS.filter(
                (option) => !option.inventoryOnly || isInventory(dataType)
              ).map((option) => this.checkbox(option, option.label(noun)))}
            </div>
          )}
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ business: state.business.business });

export default withRouter(
  connect(mapStateToProps, actions)(ImportWizardUpload)
);
